import axios from 'axios';
import { getStoreLocation } from 'shared/utils/storeLocationHelpers';
import IsEmpty from 'lodash.isempty';
import config from '../apiConfig';

/**
 * Returns a promise for a paginated list of products based on filter and sort criteria
 * Throws an exception if any required params are missing.
 * @param {axios cancellation token} cancelToken: use this to cancel a request in progress.
 * @param {ProductFilter} filters
 * 	categoryId - category that the products belong to
 *  term - keyword search (minimum three characters)
 * 	page - zero based page number
 * 	limit - number of results per page
 *  minPrice - default 0
 * 	maxPrice - default max int
 * 	orderBy - 0  (Position) - default
 *            5  (Name Ascend)
 *            6  (Name Descend)
 *            10 (Price Ascend)
 *            11 (Price Descend)
 *            15 (Created on date)
 * specAttributes - array of int: specification attributes
 * loadFilterableOptions - boolean indicating whether to load a list of applicable
 *                         filter attribute option IDs for the current search.
 *                         default is false.
 * @returns promise
 */
const getFilteredProducts = (cancelToken, filters) => {
	const instance = axios.create(config);
	// filters is a ProductFilter object with "private" variables and getters and setters.
	// Since the private variables are prefixed with an underscore, passing this directly to
	// axios or cloning into params via object assign will end up with parameters with underscores
	const params = {
		categoryId: filters.categoryId,
		limit: filters.limit,
		maxPrice: filters.maxPrice,
		minPrice: filters.minPrice,
		orderBy: filters.orderBy,
		page: filters.page,
		specAttributes: filters.specAttributes,
		term: filters.term,
		inStockOnly: filters.inStockOnly,
		onDisplayWarehouseId: filters.onDisplay ? getStoreLocation() : '',
		loadFilterableOptions: filters.loadFilterableOptions,
		stockWarehouseId: getStoreLocation()
	};
	return instance.get('FilteredProducts', {
		params,
		cancelToken,
	});
};

/**
 * Returns a promise for a single product for a given product id.
 * @param {axios cancellation token} cancelToken - cancel an ongoing request.
 * @param {int} productId
 * The cancellation token can be generated using the helper function getCancellationToken
 * @throws Error when Product ID is not supplied
 * @throws {TypeError} when non-numeric ID is supplied
 * @returns promise
 */
const getProduct = (cancelToken, productId) => {
	if (!productId) {
		throw new Error('Product id is required.');
	}
	if (typeof productId !== 'number') {
		throw new TypeError('Invalid product id.');
	}

	const instance = axios.create(config);
	const nopWarehouseId = getStoreLocation();

	return instance.get('Product', {
		params: {
			productId,
			nopWarehouseId, // "store" on the frontend is a nop warehouse on the backend
		},
		cancelToken,
	});
};

/**
 * Returns a promise for a product family and its product given a product family id
 * @param {axios cancellation token} cancelToken - cancel an ongoing request.
 * @param {string} productFamilyId
 * The cancellation token can be generated using the helper function getCancellationToken
 * @throws Error when Product family ID is not supplied
 * @throws {TypeError} when non-numeric product family ID is supplied
 * @returns promise
 */
const getProductFamily = (cancelToken, productFamilyId) => {
	if (!productFamilyId) {
		throw new Error('Product family id is required.');
	}
	if (typeof productFamilyId !== 'string') {
		throw new TypeError('Invalid product family id.');
	}

	const instance = axios.create(config);
	const nopWarehouseId = getStoreLocation();

	return instance.get('ProductFamily', {
		params: {
			productFamilyId,
			nopWarehouseId, // "store" on the frontend is a nop warehouse on the backend
		},
		cancelToken,
	});
};

const getProductOnDisplay = (cancelToken, productId, nopWarehouseId) => {
	const instance = axios.create(config);

	return instance.get('ProductOnDisplay', {
		params: {
			productId,
			nopWarehouseId,
		},
		cancelToken,
	});
};

const getProductQuantity = (cancelToken, productId, nopWarehouseId) => {
	const instance = axios.create(config);
	return instance.get('ProductQuantity', {
		params: {
			nopProductId: productId,
			nopWarehouseId,
		},
		cancelToken,
	});
};

/**
 * Returns a promise for a list of specification attributes that can be used
 * for product filtering.
 * @param {axios cancel token} cancelToken
 */
const getSpecAttributes = cancelToken => {
	const instance = axios.create(config);

	return instance.get('SpecificationAttributes', {
		cancelToken,
	});
};

/**
 * Returns an array of related products for each product ID passed in
 * @param {axios cancel token} cancelToken
 * @param {array} nopProductIds array of Nop product IDs that we want related products for
 */
const getRelatedProducts = (cancelToken, nopProductIds) => {
	const instance = axios.create(config);
	return instance.get('GetRelatedProducts', {
		params: {
			nopProductIds,
		},
		cancelToken,
	});
};

/**
 * Returns a promise for a single product for a given product id.
 * @param {axios cancellation token} cancelToken - cancel an ongoing request.
 * @param {array} productId
 * The cancellation token can be generated using the helper function getCancellationToken
 * @throws Error when Product ID is not supplied
 * @throws {TypeError} when non-numeric ID is supplied
 * @returns promise
 */
const getProductReviews = (cancelToken, nopProductIds) => {
	if (IsEmpty(nopProductIds)) {
		throw new Error('Product id is required.');
	}
	nopProductIds.forEach(id => {
		if (typeof id !== 'number') {
			throw new TypeError('Invalid product id.');
		}
	});

	const instance = axios.create(config);

	return instance.get('GetProductReviews', {
		params: {
			nopProductIds,
		},
		cancelToken,
	});
};

export {
	getFilteredProducts,
	getProduct,
	getProductFamily,
	getProductOnDisplay,
	getProductQuantity,
	getSpecAttributes,
	getRelatedProducts,
	getProductReviews,
};
